import { useState } from "react";
import { Blocks } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export type BlockchainDataVO = {
  txHash?: string | null;
  assetId?: number | string | null;
};

type ActivityBlockchainDetailsProps = {
  blockchainDataVO?: BlockchainDataVO | null;
};

function hasBlockchainData(data?: BlockchainDataVO | null) {
  if (!data || typeof data !== "object") return false;
  const hasTxHash =
    data.txHash != null && String(data.txHash).trim() !== "";
  const hasAssetId =
    data.assetId != null && String(data.assetId).trim() !== "";
  return hasTxHash || hasAssetId;
}

export function ActivityBlockchainDetails({
  blockchainDataVO,
}: ActivityBlockchainDetailsProps) {
  const [open, setOpen] = useState(false);

  if (!hasBlockchainData(blockchainDataVO)) return null;

  const txHash =
    blockchainDataVO?.txHash != null && String(blockchainDataVO.txHash).trim()
      ? String(blockchainDataVO.txHash).trim()
      : "—";
  const assetId =
    blockchainDataVO?.assetId != null &&
    String(blockchainDataVO.assetId).trim() !== ""
      ? String(blockchainDataVO.assetId)
      : "—";

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="h-8 shrink-0 gap-1.5 px-2.5 text-xs text-slate-600 hover:text-blue-700"
        onClick={() => setOpen(true)}
        title="View blockchain details"
        aria-label="View blockchain details"
      >
        <Blocks className="h-3.5 w-3.5" />
        View
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Blockchain Details</DialogTitle>
            <DialogDescription>
              Transaction hash and asset information for this activity
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="rounded-lg border border-slate-200 bg-slate-50/80 p-3">
              <p className="text-xs font-medium uppercase tracking-wider text-slate-500">
                Tx Hash
              </p>
              <p className="mt-1 break-all font-mono text-sm text-slate-900">
                {txHash}
              </p>
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50/80 p-3">
              <p className="text-xs font-medium uppercase tracking-wider text-slate-500">
                Asset ID
              </p>
              <p className="mt-1 font-mono text-sm text-slate-900">{assetId}</p>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
